/**
 * PLE Product Finder Quiz v1.0.4 — vanilla custom element, no dependencies.
 * Config: assets/quiz-config.json. Live data: /products/{handle}.js.
 * State lives in the URL — every step shareable and deep-linkable.
 */

(function () {
  'use strict';

  const MONEY = (cents, fmt) => {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return (fmt || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount);
  };

  const productCache = new Map();

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const p = fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    productCache.set(handle, p);
    return p;
  }

  /** Cheapest available variant, or null if the whole product is sold out. */
  function firstAvailableVariant(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    const available = product.variants.filter((v) => v.available);
    if (!available.length) return null;
    return available.sort((a, b) => a.price - b.price)[0];
  }

  /** Variant closest to the requested inch length; else cheapest available. */
  function variantForInches(product, inches) {
    if (!product || !inches) return firstAvailableVariant(product);
    const available = (product.variants || []).filter((v) => v.available);
    if (!available.length) return null;
    let best = null;
    let bestDelta = Infinity;
    available.forEach((v) => {
      const m = String(v.title).match(/(\d{1,2})\s*"/);
      if (!m) return;
      const delta = Math.abs(parseInt(m[1], 10) - inches);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = v;
      }
    });
    return best || available[0];
  }

  class ProductFinderQuiz extends HTMLElement {
    async connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.assetBase = this.dataset.assetBase || '';
      this.stage = this.querySelector('[data-quiz-stage]');
      this.progressEl = this.querySelector('[data-quiz-progress]');
      this.answers = this.readUrl();
      this.started = false;
      this.emailShown = false;

      this.config = await this.loadConfig();
      if (!this.config) {
        if (this.stage) {
          this.stage.innerHTML =
            '<p class="quiz-loading">We couldn\'t load the quiz. <a href="/collections/all">Browse everything instead</a>.</p>';
        }
        return;
      }

      window.addEventListener('popstate', () => {
        this.answers = this.readUrl();
        this.render();
      });

      this.render();
    }

    /** Inline JSON if present (no extra request), else assets/quiz-config.json. */
    async loadConfig() {
      const inline = this.querySelector('[data-quiz-config]');
      if (inline && inline.textContent.trim()) {
        try {
          return JSON.parse(inline.textContent);
        } catch (e) {
          console.error('[quiz] inline config parse failed', e);
        }
      }

      const url = this.dataset.configUrl;
      if (!url) return null;

      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(res.status);
        return await res.json();
      } catch (e) {
        console.error('[quiz] config fetch failed', e);
        return null;
      }
    }

    /* --- state --- */

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      const answers = {};
      params.forEach((value, key) => {
        if (key === 'length' && value.includes(',')) answers[key] = value.split(',');
        else answers[key] = value;
      });
      return answers;
    }

    writeUrl(replace) {
      const params = new URLSearchParams();
      Object.keys(this.answers).forEach((k) => {
        const v = this.answers[k];
        if (v === undefined || v === null || v === '') return;
        params.set(k, Array.isArray(v) ? v.join(',') : v);
      });
      const url = `${window.location.pathname}?${params.toString()}`;
      if (replace) window.history.replaceState({}, '', url);
      else window.history.pushState({}, '', url);
    }

    /** Questions that apply given the answers so far. */
    activeQuestions() {
      return this.config.questions.filter((q) => {
        if (q.showIf && !this.matches(q.showIf)) return false;
        if (q.skipIf && this.matches(q.skipIf)) return false;
        return true;
      });
    }

    matches(cond) {
      return Object.keys(cond).every((key) => {
        const want = cond[key];
        const got = this.answers[key];
        if (Array.isArray(want)) return want.includes(got);
        return want === got;
      });
    }

    currentQuestion() {
      return this.activeQuestions().find((q) => !this.answers[q.key]) || null;
    }

    /* --- answers --- */

    answer(key, value, multi) {
      if (multi) {
        const list = Array.isArray(this.answers[key]) ? this.answers[key].slice() : [];
        const i = list.indexOf(value);
        if (i > -1) list.splice(i, 1);
        else list.push(value);
        this.answers[key] = list;
        this.writeUrl(true);
        this.render();
        return;
      }

      this.answers[key] = value;

      // Clear answers to questions that no longer apply (e.g. switching q1).
      const validKeys = this.activeQuestions().map((q) => q.key);
      Object.keys(this.answers).forEach((k) => {
        if (k !== 'audience' && k !== 'email' && !validKeys.includes(k)) delete this.answers[k];
      });

      this.writeUrl(false);
      this.track('quiz_step_completed', { question_key: key, answer_value: value });
      this.render();
    }

    /* --- render --- */

    render() {
      if (!this.answers.audience) return this.renderAudience();

      if (!this.started) {
        this.started = true;
        this.track('quiz_started', { entry_point: document.referrer.includes(location.host) ? 'internal' : 'direct' });
      }

      const question = this.currentQuestion();
      if (question) return this.renderQuestion(question);

      const gate = this.config.emailGate;
      if (gate && gate.enabled && !this.emailShown && !this.answers.email) {
        this.emailShown = true;
        return this.renderEmailGate();
      }

      return this.renderResults();
    }

    setProgress(step, total) {
      if (!this.progressEl) return;
      if (!total) {
        this.progressEl.innerHTML = '';
        return;
      }
      const pct = Math.round((step / total) * 100);
      this.progressEl.innerHTML =
        `<div class="quiz-progress__bar"><span style="width:${pct}%"></span></div>` +
        `<p class="quiz-progress__label">Step ${step} of ${total}</p>`;
    }

    renderAudience() {
      const a = this.config.audience;
      this.setProgress(0, 0);
      this.stage.innerHTML = `
        <div class="quiz-step quiz-step--audience">
          <h1 class="quiz-step__title">${a.title}</h1>
          ${a.subtitle ? `<p class="quiz-step__subtitle">${a.subtitle}</p>` : ''}
          <div class="quiz-options quiz-options--2">
            ${a.options.map((o) => this.optionMarkup(o, false, null, true)).join('')}
          </div>
        </div>`;
      this.bindOptions(a.key, false);
    }

    renderQuestion(q) {
      const list = this.activeQuestions();
      const index = list.findIndex((x) => x.key === q.key);
      this.setProgress(index + 1, list.length);

      const isPro = this.answers.audience === 'pro';
      const title = isPro && q.titlePro ? q.titlePro : q.title;
      const multi = q.multiSelectIf ? this.matches(q.multiSelectIf) : false;
      const cols = q.options.length <= 2 ? 2 : q.options.length === 3 ? 3 : 3;

      this.stage.innerHTML = `
        <div class="quiz-step">
          ${index > 0 || this.answers.audience ? '<button type="button" class="quiz-back" data-quiz-back>← Back</button>' : ''}
          <h2 class="quiz-step__title">${title}</h2>
          ${q.subtitle ? `<p class="quiz-step__subtitle">${q.subtitle}</p>` : ''}
          <div class="quiz-options quiz-options--${cols}">
            ${q.options.map((o) => this.optionMarkup(o, multi, this.answers[q.key])).join('')}
          </div>
          ${multi ? '<button type="button" class="quiz-next" data-quiz-next>Continue</button>' : ''}
        </div>`;

      this.bindOptions(q.key, multi);

      const back = this.stage.querySelector('[data-quiz-back]');
      if (back) back.addEventListener('click', () => window.history.back());

      const next = this.stage.querySelector('[data-quiz-next]');
      if (next) {
        next.addEventListener('click', () => {
          if (!this.answers[q.key] || !this.answers[q.key].length) return;
          this.writeUrl(false);
          this.render();
        });
      }
    }

    optionMarkup(o, multi, current, eager) {
      const selected = multi
        ? Array.isArray(current) && current.includes(o.value)
        : current === o.value;

      const visual = o.swatch
        ? `<span class="quiz-option__swatch" style="background:${o.swatch}"></span>`
        : o.image
        ? `<span class="quiz-option__media"><img src="${this.imageSrc(o.image)}" alt="" ${eager ? 'loading="eager" fetchpriority="high" width="400" height="500"' : 'loading="lazy" width="400" height="400"'} onerror="this.closest('.quiz-option__media').remove()"></span>`
        : '';

      return `
        <button type="button"
                class="quiz-option${selected ? ' is-selected' : ''}${o.wide ? ' quiz-option--wide' : ''}"
                data-quiz-option="${o.value}"
                aria-pressed="${selected ? 'true' : 'false'}">
          ${visual}
          <span class="quiz-option__label">${o.label}</span>
          ${o.sublabel ? `<span class="quiz-option__sublabel">${o.sublabel}</span>` : ''}
        </button>`;
    }

    /** Absolute URLs pass through; bare filenames get the asset base prefixed. */
    imageSrc(image) {
      return /^(https?:)?\/\//.test(image) ? image : this.assetBase + image;
    }

    bindOptions(key, multi) {
      this.stage.querySelectorAll('[data-quiz-option]').forEach((btn) => {
        btn.addEventListener('click', () => this.answer(key, btn.dataset.quizOption, multi));
      });
    }

    /* --- email gate --- */

    renderEmailGate() {
      const g = this.config.emailGate;
      this.setProgress(0, 0);
      this.stage.innerHTML = `
        <div class="quiz-step quiz-step--gate">
          <h2 class="quiz-step__title">${g.headline}</h2>
          <p class="quiz-step__subtitle">${g.body}</p>
          <form class="quiz-gate" data-quiz-gate novalidate>
            <label class="visually-hidden" for="quiz-email">Email address</label>
            <input type="email" id="quiz-email" name="email" required placeholder="you@email.com" autocomplete="email">
            <button type="submit" class="quiz-cta">${g.cta}</button>
          </form>
          <button type="button" class="quiz-skip" data-quiz-skip>${g.skipLabel}</button>
          <p class="quiz-consent">${g.consentText}</p>
        </div>`;

      const form = this.stage.querySelector('[data-quiz-gate]');
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const email = form.querySelector('input[name="email"]').value.trim();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          form.querySelector('input').classList.add('is-error');
          return;
        }
        this.answers.email = email;
        this.identify(email);
        this.render();
      });

      this.stage.querySelector('[data-quiz-skip]').addEventListener('click', () => {
        this.track('quiz_email_skipped', {});
        this.render();
      });
    }

    /* --- results --- */

    /** Highest-scoring rule wins; ties break toward the later (more specific) rule. */
    resolveRule() {
      let best = null;
      let bestScore = -1;
      this.config.rules.forEach((rule) => {
        const keys = Object.keys(rule.match);
        const ok = keys.every((k) => {
          const want = rule.match[k];
          const got = this.answers[k];
          return Array.isArray(want) ? want.includes(got) : want === got;
        });
        if (ok && keys.length >= bestScore) {
          bestScore = keys.length;
          best = rule;
        }
      });
      return best;
    }

    candidateHandles(rule) {
      if (!rule.heroes) return [];
      const texture = this.answers.texture || 'any';
      const list = (rule.heroes[texture] || []).concat(rule.heroes.any || []);
      const exclude = rule.excludeHandles || [];
      return list.filter((h, i) => list.indexOf(h) === i && !exclude.includes(h));
    }

    inchesWanted() {
      const opt = this.config.questions.find((q) => q.key === 'length');
      if (!opt) return null;
      const val = Array.isArray(this.answers.length) ? this.answers.length[0] : this.answers.length;
      const found = opt.options.find((o) => o.value === val);
      return found ? found.inches : null;
    }

    async renderResults() {
      this.setProgress(0, 0);
      this.stage.innerHTML = '<div class="quiz-loading" role="status">Finding your match…</div>';

      const rule = this.resolveRule();
      if (!rule) {
        this.stage.innerHTML = this.fallbackMarkup('/collections/all');
        return;
      }

      const isPro = this.answers.audience === 'pro';
      const inches = this.inchesWanted();
      const handles = this.candidateHandles(rule);

      // Fetch candidates and drop anything sold out — the stock-aware fallback.
      const fetched = await Promise.all(handles.map(fetchProduct));
      const live = fetched
        .map((p, i) => ({ product: p, handle: handles[i] }))
        .filter((x) => x.product && firstAvailableVariant(x.product))
        .map((x) => ({ ...x, variant: variantForInches(x.product, inches) }));

      if (!live.length) {
        this.stage.innerHTML = this.fallbackMarkup(`/collections/${rule.collection}`, rule);
        this.track('quiz_completed', { ...this.answers, result: rule.label, fallback: true });
        return;
      }

      const hero = live[0];
      const alternates = live.slice(1, 4);
      const addonKey = this.answers.part;
      const addon = addonKey && this.config.addons[addonKey] ? this.config.addons[addonKey] : null;

      let html = `
        <div class="quiz-results">
          <p class="quiz-results__eyebrow">Your match</p>
          <div class="quiz-hero">
            <a class="quiz-hero__media" href="/products/${hero.handle}" data-quiz-result="${hero.handle}" data-position="hero">
              <img src="${hero.product.featured_image}&width=800" alt="${hero.product.title}" width="800" height="800">
            </a>
            <div class="quiz-hero__body">
              <h2 class="quiz-hero__title">
                <a href="/products/${hero.handle}" data-quiz-result="${hero.handle}" data-position="hero">${hero.product.title}</a>
              </h2>
              <p class="quiz-hero__price">${MONEY(hero.variant ? hero.variant.price : hero.product.price, this.moneyFormat)}</p>
              <p class="quiz-hero__reason">${rule.reason}</p>
              <a class="quiz-cta" href="/products/${hero.handle}" data-quiz-result="${hero.handle}" data-position="hero-cta">
                ${isPro ? 'Add to order' : 'Shop this'}
              </a>
            </div>
          </div>`;

      if (alternates.length) {
        html += `
          <section class="quiz-alts">
            <h3 class="quiz-section__title">You might also like</h3>
            <div class="quiz-grid">
              ${alternates
                .map(
                  (a, i) => `
                <a class="quiz-card" href="/products/${a.handle}" data-quiz-result="${a.handle}" data-position="alt-${i + 1}">
                  <img src="${a.product.featured_image}&width=400" alt="${a.product.title}" width="400" height="400" loading="lazy">
                  <span class="quiz-card__title">${a.product.title}</span>
                  <span class="quiz-card__price">${MONEY(a.variant ? a.variant.price : a.product.price, this.moneyFormat)}</span>
                </a>`
                )
                .join('')}
            </div>
          </section>`;
      }

      html += `<div data-quiz-addon></div><div data-quiz-deal></div>`;

      html += `
          <div class="quiz-results__footer">
            <a class="quiz-textlink" href="/collections/${rule.collection}">See all ${rule.label.toLowerCase()}</a>
            <button type="button" class="quiz-textlink" data-quiz-restart>Change my answers</button>
          </div>`;

      if (isPro) {
        const pl = this.config.proLinks;
        html += `
          <aside class="quiz-pro">
            <h3>${pl.label}</h3>
            <p>${pl.blurb}</p>
            <a class="quiz-cta quiz-cta--ghost" href="${pl.wholesaleUrl}">${pl.label}</a>
          </aside>`;
      } else {
        html += `
          <p class="quiz-b2b">
            Shopping for your business?
            <a href="${this.config.proLinks.wholesaleUrl}">See stylist and wholesale pricing</a>.
          </p>`;
      }

      html += '</div>';
      this.stage.innerHTML = html;

      this.bindResultTracking();
      if (addon) this.renderAddon(addon);
      if (rule.bundleDeal) this.renderBundleDeal(rule, hero);

      this.track('quiz_completed', { ...this.answers, result: rule.label, hero: hero.handle });
      this.identify(this.answers.email, rule, hero.handle);
    }

    async renderAddon(addon) {
      const mount = this.stage.querySelector('[data-quiz-addon]');
      if (!mount) return;
      mount.innerHTML = `
        <section class="quiz-addon">
          <h3 class="quiz-section__title">${addon.title}</h3>
          <p class="quiz-section__blurb">${addon.blurb}</p>
          <a class="quiz-cta quiz-cta--ghost" href="/collections/${addon.collection}">Browse ${addon.title.toLowerCase()}</a>
        </section>`;
    }

    /**
     * Bundle Deal module — dynamic.
     * Renders ONLY when the multi-pack actually beats buying singles. While the
     * repricing pass is in flight this keeps itself hidden on any collection
     * where the maths doesn't work, and turns itself on as prices land.
     */
    async renderBundleDeal(rule, hero) {
      const mount = this.stage.querySelector('[data-quiz-deal]');
      if (!mount || !rule.bundleDeal) return;

      const dealHandle = rule.bundleDeal.handle || `${hero.handle}-bundle-deals`;
      const deal = await fetchProduct(dealHandle);
      if (!deal) return;

      const dealVariant = firstAvailableVariant(deal);
      const singleVariant = hero.variant || firstAvailableVariant(hero.product);
      if (!dealVariant || !singleVariant) return;

      const count = rule.bundleDeal.bundleCount || 3;
      const saving = singleVariant.price * count - dealVariant.price;

      if (saving <= 0) return; // suppress — never show a "deal" that isn't one

      const isPro = this.answers.audience === 'pro';
      mount.innerHTML = `
        <section class="quiz-deal${isPro ? ' quiz-deal--primary' : ''}">
          <h3 class="quiz-section__title">Save ${MONEY(saving, this.moneyFormat)} with the ${count}-pack</h3>
          <p class="quiz-section__blurb">${deal.title} — ${MONEY(dealVariant.price, this.moneyFormat)} for ${count} bundles.</p>
          <a class="quiz-cta" href="/products/${dealHandle}" data-quiz-result="${dealHandle}" data-position="deal">Shop the ${count}-pack</a>
        </section>`;
      this.bindResultTracking();
    }

    fallbackMarkup(url, rule) {
      return `
        <div class="quiz-results quiz-results--fallback">
          <p class="quiz-results__eyebrow">Closest matches</p>
          <h2 class="quiz-step__title">We're restocking your exact match</h2>
          <p class="quiz-step__subtitle">
            Here's everything closest to what you picked${rule ? ` in ${rule.label.toLowerCase()}` : ''}.
          </p>
          <a class="quiz-cta" href="${url}">See closest matches</a>
          <button type="button" class="quiz-textlink" data-quiz-restart>Change my answers</button>
        </div>`;
    }

    bindResultTracking() {
      this.stage.querySelectorAll('[data-quiz-result]').forEach((el) => {
        if (el.dataset.bound) return;
        el.dataset.bound = '1';
        el.addEventListener('click', () => {
          this.track('quiz_result_clicked', {
            product_handle: el.dataset.quizResult,
            position: el.dataset.position
          });
        });
      });

      const restart = this.stage.querySelector('[data-quiz-restart]');
      if (restart) {
        restart.addEventListener('click', () => {
          ['texture', 'length', 'tier', 'color'].forEach((k) => delete this.answers[k]);
          this.emailShown = false;
          this.writeUrl(false);
          this.render();
        });
      }
    }

    /* --- tracking --- */

    track(event, payload) {
      const data = Object.assign({ quiz_version: 'v1' }, payload);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, data));

      if (typeof window.gtag === 'function') window.gtag('event', event, data);

      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', event, data]);
      }

      this.dispatchEvent(new CustomEvent(`quiz:${event}`, { detail: data, bubbles: true }));
    }

    /** Write quiz answers onto the Klaviyo profile as durable properties. */
    identify(email, rule, heroHandle) {
      if (!window.klaviyo || typeof window.klaviyo.push !== 'function') return;

      const props = {
        quiz_audience: this.answers.audience || null,
        quiz_install_type: this.answers.q1 || null,
        quiz_texture: this.answers.texture || null,
        quiz_length: Array.isArray(this.answers.length) ? this.answers.length.join(',') : this.answers.length || null,
        quiz_tier: this.answers.tier || null,
        quiz_completed_at: new Date().toISOString()
      };
      if (rule) props.quiz_result_label = rule.label;
      if (heroHandle) props.quiz_recommended_handle = heroHandle;
      if (email) props.$email = email;

      window.klaviyo.push(['identify', props]);
    }
  }

  if (!customElements.get('product-finder-quiz')) {
    customElements.define('product-finder-quiz', ProductFinderQuiz);
  }
})();
